export type MatchMode = 'exact' | 'not_equal' | 'partial' | 'starts_with' | 'greater_than' | 'less_than' | 'greater_than_or_equal' | 'less_than_or_equal' | 'between';

export interface FilterCriteria {
  values: (string | number | boolean)[];
  matchMode?: MatchMode;  // Optional - defaults to 'exact'
}

export interface DynamicTableFilters {
  [fieldId: number]: FilterCriteria;
}

export class DynamicTableEntriesDto {
  public applicationId: number;
  public datasetId: number;
  public rowData: any[];
}

export interface SortCriteria {
  fieldId: number;
  direction: 'asc' | 'desc';
}

export type AggregationFunction = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'count_distinct';

export interface AggregationCriteria {
  fieldId: number;              // 0 together with 'count' asks for a row count per group
  function: AggregationFunction;
  alias?: string;               // Output column name. Defaults to `${fieldName}_${function}`.
}

// Output column the API gives a row-count aggregation (fieldId 0, function 'count').
export const ROW_COUNT_ALIAS = 'Row_Count';

/**
 * The output column an aggregate lands in, mirroring the API's default alias. Callers
 * need it to read the aggregated value back out of a grouped row.
 */
export function aggregationAlias(fieldName: string, aggregationFunction: AggregationFunction): string {
  return `${fieldName}_${aggregationFunction}`;
}

export class DynamicTableSelectDto {
  public applicationId: number;
  public datasetId: number;
  public limit: number = -1;
  public offset?: number; // Offset for pagination (number of rows to skip)
  public filters?: DynamicTableFilters;
  public distinct?: boolean; // Return only distinct rows (default: false)
  public orderBy?: SortCriteria[]; // Server-side sorting by one or more fields
  public fields?: number[]; // Only return these fields (by fieldId). Empty/null = all fields (SELECT *).
  public groupBy?: number[]; // Group by these fields. The result is one row per distinct combination.
  public aggregations?: AggregationCriteria[]; // Aggregates returned alongside the groupBy columns.
}

export class GetValuesForFieldDto {
  public datasetId: number;
}

export class ImportFromGCSDto {
  public datasetId: number;
  public filePath: string;
  public delimiter?: string; // Optional delimiter for CSV files (default: comma)
}

export class DynamicTableEntriesResponse {
  rowData: any[];
  primaryKeyFieldName: string;
  totalCount?: number; // Total number of records in the dataset (for pagination)
}

export class FieldValuesResponse {
  values: any[];
}

export class ClearDataDto {
  public datasetId: number;
  public fieldId: number;
  public value: string;
}

export class ClearDataResponse {
  public deletedCount: number;
}

export class DynamicTableRowCountDto {
  public datasetId: number;
  public filters?: DynamicTableFilters;
  public groupBy?: number[]; // Counts the groups rather than the rows - the row count of a grouped select.
}

export class DynamicTableRowCountResponse {
  public rowCount: number;
}

export class ExportToGCSDto {
  public applicationId: number;
  public datasetId: number;
  public filePath: string;
  public fileFormat?: string;
  public delimiter?: string;
  // The same criteria a select takes, so the file matches what the caller is looking at
  public filters?: DynamicTableFilters;
  public fields?: number[];
  public groupBy?: number[];
  public aggregations?: AggregationCriteria[];
}

export class ExportToGCSResponse {
  public filePath: string;
  public rowCount: number;
  public fileFormat: string;
}
